import { usePatchBatchCandidates } from "@api";
import type { IDocument } from "@bbj-types/document";
import type { ObjectId } from "@bbj-types/utils";
import { CreateAction } from "@root/utils/enums/communication";
import { useEffect, useMemo, useState } from "react";
import { SendMailStep } from "./stepper";
import type { IRecipient, TSendMailModalCandidate } from "./type";

export const useSendMailModal = (
  candidates: TSendMailModalCandidate[],
  onSubmit: (templateId: IDocument, candidateIds: ObjectId[], coachingStep?: string) => Promise<void>,
  onClose: () => void,
  onParticipantsModified?: (modifiedParticipants: Set<string>) => void
) => {
  const [{ loading }, updateCandidates] = usePatchBatchCandidates();
  const [step, setStep] = useState(SendMailStep.RECIPIENTS);
  const [coachingStepChange, setCoachingStepChange] = useState<string | undefined>();
  const [createAction, setCreateAction] = useState<CreateAction>(CreateAction.NONE);
  const [recipients, setRecipients] = useState<IRecipient[]>([]);
  const [template, setTemplate] = useState<IDocument | null>(null);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);

  const invalidRecipientsCount = useMemo(
    () => recipients.filter((recipient) => recipient.invalidAddress).length,
    [recipients]
  );

  const handleClose = () => {
    onClose();
    setStep(SendMailStep.RECIPIENTS);
    setTemplate(null);
    setCreateAction(CreateAction.NONE);
  };

  const handleSubmit = async () => {
    if (template) {
      const candidateIds = recipients.reduce<ObjectId[]>((recipients, recipient) => {
        if (recipient.invalidAddress) {
          return recipients;
        }
        recipients.push(recipient.candidate._id);
        return recipients;
      }, []);

      if (coachingStepChange) {
        await updateCandidates({
          candidateIds,
          field: "coachingStep",
          value: coachingStepChange,
        });
      }
      await onSubmit(template, candidateIds, coachingStepChange);

      if (onParticipantsModified) {
        onParticipantsModified(new Set(candidateIds.map(String)));
      }
      handleClose();
    }
  };

  useEffect(() => {
    setRecipients(candidates.map((candidate) => ({ candidate, invalidAddress: !candidate.address?.street })));
  }, [candidates]);

  return {
    step,
    setStep,
    coachingStepChange,
    setCoachingStepChange,
    createAction,
    setCreateAction,
    recipients,
    setRecipients,
    template,
    setTemplate,
    templatePreviewLoading,
    setTemplatePreviewLoading,
    invalidRecipientsCount,
    handleSubmit,
    loading,
    handleClose,
  };
};
